import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Globe, AlertCircle, CheckCircle2, ExternalLink, Cookie as CookieIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Componente dinamico che mostra il registro dei responsabili del trattamento.
 * Viene fetchato ogni volta dalle pagine Privacy/Cookie Policy — garantisce che
 * la documentazione sia sempre allineata al codice.
 */
export default function PrivacyRegistry({ showCookies = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/privacy/registry`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Impossibile caricare il registro"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-xs text-zinc-400 py-4">Caricamento registro...</div>;
  if (error) return <div className="text-xs text-red-500 py-4">{error}</div>;
  if (!data) return null;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "";
  // Raggruppa per categoria
  const byCategory = data.processors.reduce((acc, p) => {
    (acc[p.categoria] = acc[p.categoria] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="privacy-registry">
      {/* VERSION BANNER */}
      <div className="bg-[#E8F0EA] border border-[#163321]/20 rounded-xl p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-[#163321] shrink-0 mt-0.5" />
        <div className="text-xs text-[#163321]">
          <strong>Documento allineato con il codice.</strong> Versione <code className="bg-white px-1.5 py-0.5 rounded text-[10px] font-mono">{data.version}</code> · Firma <code className="bg-white px-1.5 py-0.5 rounded text-[10px] font-mono">{data.hash}</code>
          {data.last_change_detected_at && (
            <span> · Ultima modifica rilevata: <strong>{fmtDate(data.last_change_detected_at)}</strong></span>
          )}
          <p className="text-[11px] text-[#4A5E51] mt-1">Questo elenco e' generato dinamicamente dal codice dell'applicazione ogni volta che apri la pagina. Se viene aggiunto o rimosso un fornitore, la firma cambia e la data si aggiorna automaticamente.</p>
        </div>
      </div>

      {/* CONTROLLER */}
      <div className="bg-white border border-[#E5E2DB] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-wider text-[#4A5E51] font-bold mb-2 flex items-center gap-1">
          <Shield className="w-3 h-3" /> Titolare del Trattamento
        </p>
        <p className="text-sm font-medium text-[#0F1F14]">{data.controller.ragione_sociale}</p>
        <p className="text-xs text-[#4A5E51]">
          {data.controller.sede_legale} · P.IVA {data.controller.partita_iva} · {data.controller.iscrizione_ivass}
        </p>
        <p className="text-xs text-[#4A5E51] mt-1">
          Privacy: <a href={`mailto:${data.controller.email_privacy}`} className="text-[#163321] underline">{data.controller.email_privacy}</a>
          {" · "}PEC: <span className="text-[#163321]">{data.controller.pec}</span>
        </p>
      </div>

      {/* PROCESSORS GROUPED BY CATEGORY */}
      <div className="space-y-4">
        <h3 className="font-heading text-base text-[#0F1F14] font-medium">
          Responsabili del trattamento attivi ({data.processors_count})
        </h3>
        {Object.entries(byCategory).map(([cat, items]) => (
          <div key={cat} className="border border-[#E5E2DB] rounded-xl overflow-hidden" data-testid={`registry-category-${cat.replace(/ /g, '-').toLowerCase()}`}>
            <div className="bg-[#F5F8F6] px-4 py-2 border-b border-[#E5E2DB]">
              <p className="text-xs uppercase tracking-wider text-[#163321] font-bold">{cat}</p>
            </div>
            <div className="divide-y divide-[#E5E2DB]">
              {items.map(p => (
                <details key={p.id} className="p-4 hover:bg-[#F5F8F6]/50" data-testid={`registry-processor-${p.id}`}>
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0F1F14]">{p.nome}</p>
                        <p className="text-xs text-[#4A5E51] mt-0.5">{p.servizio}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-[#4A5E51] bg-white px-2 py-0.5 rounded-full border border-[#E5E2DB] whitespace-nowrap flex items-center gap-1">
                        <Globe className="w-2.5 h-2.5" />{p.paese.split(" ")[0]}
                      </span>
                    </div>
                  </summary>
                  <div className="mt-3 space-y-2 pl-1 text-xs text-[#4A5E51]">
                    {p.dati_trattati.length > 0 && (
                      <div>
                        <strong className="text-[#0F1F14]">Dati trattati:</strong>
                        <ul className="list-disc pl-5 mt-1 space-y-0.5">
                          {p.dati_trattati.map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      </div>
                    )}
                    <p><strong className="text-[#0F1F14]">Base giuridica:</strong> {p.base_giuridica}</p>
                    <p><strong className="text-[#0F1F14]">Finalita':</strong> {p.finalita}</p>
                    <p><strong className="text-[#0F1F14]">Paese / Trasferimento:</strong> {p.paese}</p>
                    {p.note && (
                      <div className="bg-[#FFF7E8] border border-[#E8D3A0]/60 rounded-lg p-2">
                        <AlertCircle className="w-3 h-3 inline mr-1 text-[#B8820E]" />
                        <span className="text-[#8B6100]">{p.note}</span>
                      </div>
                    )}
                    {p.privacy_url && (
                      <a href={p.privacy_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#163321] underline hover:no-underline">
                        <ExternalLink className="w-3 h-3" /> Privacy Policy del fornitore
                      </a>
                    )}
                    {p.registered && <p className="text-[10px] text-zinc-400">Registrato il {fmtDate(p.registered)}</p>}
                  </div>
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* COOKIES (mostrato solo su Cookie Policy) */}
      {showCookies && data.cookies?.length > 0 && (
        <div className="border border-[#E5E2DB] rounded-xl overflow-hidden" data-testid="registry-cookies">
          <div className="bg-[#F5F8F6] px-4 py-2 border-b border-[#E5E2DB]">
            <p className="text-xs uppercase tracking-wider text-[#163321] font-bold flex items-center gap-1">
              <CookieIcon className="w-3.5 h-3.5" /> Elenco cookie ({data.cookies.length})
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-white border-b border-[#E5E2DB]">
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#4A5E51] font-medium">Nome</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#4A5E51] font-medium">Fornitore</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#4A5E51] font-medium">Tipo</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#4A5E51] font-medium">Durata</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E2DB]">
                {data.cookies.map((c, i) => (
                  <tr key={i} className="hover:bg-[#F5F8F6]/50">
                    <td className="px-3 py-2 font-mono text-[11px] text-[#163321]">{c.nome}</td>
                    <td className="px-3 py-2 text-[#4A5E51]">{c.fornitore}</td>
                    <td className="px-3 py-2 text-[#4A5E51]">{c.tipo}</td>
                    <td className="px-3 py-2 text-[#4A5E51]">{c.durata}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
